import { t, useLanguage } from '../../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, View, Text, ActivityIndicator } from 'react-native';
import { Button, Card, Chip, Snackbar, useTheme } from 'react-native-paper';
import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { firestoreCache, CACHE_KEYS } from '../../utils/firestoreCache';
import { Course } from '../../types';
import { getCourseId, offeringDocument, getOfferingKey } from '../../utils/courseIdentity';
import { reviewIndexDocument } from '../../utils/reviewIndex';
import { getCurrentSemester, isReviewPeriodOpen } from '../../constants/reviewPeriods';
import { CourseReview, ExamType, ReviewerFaculty, ReviewerYear } from '../../types/review';
import { useReviewTags } from '../../hooks/useReviewTags';
import { useSchedule } from '../../contexts/ScheduleContext';
import { useReviewGateContext } from '../../contexts/ReviewGateContext';

type Props = {
  navigation: any;
  route: {
    params: {
      course: Course;
    };
  };
};

const getAcademicYearValue = (course: Course): string => {
  if (course.academicYear) return String(course.academicYear).replace(/[^0-9]/g, '');
  return String(new Date().getFullYear());
};

const SCALE_VALUES = [1, 2, 3, 4, 5] as const;

// 課題の重さ、テスト難易度用：淡いスレートブルーから濃いスレートブルーへのグラデーション
const BLUE_GRAY_SCALE = [
  '#A4B8CF', // 1: 淡いスレートブルー
  '#8CA5C1', // 2
  '#7492B3', // 3
  '#5C7FA5', // 4
  '#4A607A', // 5: 濃いスレートブルー
] as const;

// 授業の形式用：スレートブルー（座学）からアプリコット（対話）へのグラデーション
const COURSE_FORMAT_SCALE = [
  '#4A607A', // 1: スレートブルー (座学)
  '#5B6E6E', // 2
  '#7C7C62', // 3
  '#8E6C3B', // 4
  '#8F5E1E', // 5: アプリコット/オレンジ (対話)
] as const;

const isValidReviewerYear = (value: unknown): value is ReviewerYear => {
  return typeof value === 'number' && [1, 2, 3, 4, 5, 6].includes(value);
};

const isValidReviewerFaculty = (value: unknown): value is ReviewerFaculty => {
  return typeof value === 'string' && ['lc', 'ia', 'js', 'pg'].includes(value);
};

const getSubmitErrorMessage = (error: unknown): string => {
  const code = (error as { code?: string } | null)?.code;
  switch (code) {
    case 'permission-denied':
      return '権限がないため投稿できませんでした。プロフィールや選択内容をご確認のうえ、再度お試しください。';
    case 'unavailable':
      return 'ネットワークに接続できませんでした。通信環境を確認して再度お試しください。';
    case 'not-found':
      return '対象の授業データが見つかりませんでした。時間割から授業を削除し、再度追加してからお試しください。';
    default:
      return '投稿・更新に失敗しました。時間をおいて再度お試しください。';
  }
};

type ScaleSelectorProps = {
  label: string;
  minLabel: string;
  maxLabel: string;
  value: number | null;
  onChange: (v: number) => void;
  colors: readonly string[];
};

const ScaleSelector = ({ label, minLabel, maxLabel, value, onChange, colors }: ScaleSelectorProps) => {
  useLanguage();
  const theme = useTheme();

  return (
    <View style={styles.selectorSection}>
      <Text style={[styles.fieldTitle, { color: theme.colors.onSurface }]}>{t(label)}</Text>
      <View style={styles.scaleRow}>
        <Text style={[styles.scaleTip, { flex: 1, textAlign: 'right', color: theme.colors.onSurfaceVariant }]}>{t(minLabel)}</Text>
        <View style={styles.buttonGroup}>
          {SCALE_VALUES.map((v, index) => {
            const selected = value === v;
            const baseColor = colors[index];
            return (
              <Pressable
                key={`${label}-${v}`}
                onPress={() => onChange(v)}
                style={[
                  styles.scaleButton,
                  {
                    backgroundColor: selected ? baseColor : theme.colors.surface,
                    borderColor: baseColor,
                    borderWidth: selected ? 0 : 2.5,
                    transform: [{ scale: selected ? 1.15 : 1 }],
                  },
                  selected && {
                    shadowColor: baseColor,
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.4,
                    shadowRadius: 3,
                    elevation: 4,
                  }
                ]}
              />
            );
          })}
        </View>
        <Text style={[styles.scaleTip, { flex: 1, textAlign: 'left', color: theme.colors.onSurfaceVariant }]}>{t(maxLabel)}</Text>
      </View>
    </View>
  );
};

const CourseReviewFormScreen = ({ navigation, route }: Props) => {
  useLanguage();
  const theme = useTheme();
  const { course } = route.params;
  const { tags, loading: tagsLoading } = useReviewTags();
  const { isCourseAdded } = useSchedule();
  const { notifyReviewSubmitted } = useReviewGateContext();

  const [workload, setWorkload] = useState<number | null>(null);
  const [format, setFormat] = useState<number | null>(null);
  const [examDifficulty, setExamDifficulty] = useState<number | null>(null);
  const [examType, setExamType] = useState<ExamType | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [showTagLimitSnack, setShowTagLimitSnack] = useState(false);
  const [reviewerProfile, setReviewerProfile] = useState<{ year: ReviewerYear; faculty: ReviewerFaculty } | null>(null);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [existingReviewCreatedAt, setExistingReviewCreatedAt] = useState<unknown>(undefined);

  const academicYear = useMemo(() => getAcademicYearValue(course), [course]);
  const semesterDisplay = course.semester || '不明';
  const semesterDbValue = useMemo(() => getCurrentSemester().semester, []);
  const [periodClosed, setPeriodClosed] = useState(
    () => !isReviewPeriodOpen(academicYear, semesterDbValue)
  );

  const atmosphereTags = useMemo(() => tags.filter((tag) => tag.category === 'class_atmosphere'), [tags]);
  const contentTags = useMemo(() => tags.filter((tag) => tag.category !== 'class_atmosphere'), [tags]);

  const dayPeriodString = useMemo(() => {
    const isOutOfGrid = !course.period || course.period < 1 || course.period > 6 ||
      course.dayOfWeek === undefined || course.dayOfWeek < 1 || course.dayOfWeek > 5;
    const hasOnDemandTag = (course.name || '').includes('オンデマンド') ||
      (course.name || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
      (course.room || '').includes('オンデマンド') ||
      (course.room || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
      (course.notes || '').includes('オンデマンド') ||
      (course.notes || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ');
    const isOnDemand = isOutOfGrid || hasOnDemandTag;

    if (isOnDemand) {
      return 'オンデマンド';
    }
    if (course.dayOfWeek !== undefined && course.period !== undefined) {
      const days = ['', '月', '火', '水', '木', '金', '土', '日'];
      const dayStr = days[course.dayOfWeek] || '';
      return `${dayStr}曜${course.period}限`;
    }
    return '';
  }, [course]);

  useEffect(() => {
    if (!isReviewPeriodOpen(academicYear, semesterDbValue)) {
      setPeriodClosed(true);
      return;
    }
    setPeriodClosed(false);

    if (!auth.currentUser) {
      Alert.alert(t("ログインが必要です"), t("レビュー投稿にはログインが必要です"));
      navigation.goBack();
      return;
    }

    const checkAndLoadExistingReview = async () => {
      if (!auth.currentUser) return;
      try {
        setLoadingExisting(true);

        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        const userYear = Number(userData?.year);
        const userFaculty = userData?.faculty;

        if (!isValidReviewerYear(userYear) || !isValidReviewerFaculty(userFaculty)) {
          setProfileIncomplete(true);
          return;
        }

        setProfileIncomplete(false);
        setReviewerProfile({ year: userYear, faculty: userFaculty });

        const reviewRef = doc(offeringDocument(db, course), 'reviews', auth.currentUser.uid);
        const reviewDoc = await getDoc(reviewRef);
        if (reviewDoc.exists()) {
          const data = reviewDoc.data() as Partial<CourseReview>;
          setHasExistingReview(true);
          setExistingReviewCreatedAt(data.createdAt);
          setWorkload(typeof data.workload === 'number' ? data.workload : null);
          setFormat(typeof data.format === 'number' ? data.format : null);
          setExamDifficulty(typeof data.examDifficulty === 'number' ? data.examDifficulty : null);
          setExamType((data.examType as ExamType) || null);
          setSelectedTags(Array.isArray(data.tags) ? data.tags.slice(0, 4) : []);
        }
      } catch (error) {
        console.error('[CourseReviewFormScreen] Failed to check/load review:', error);
      } finally {
        setLoadingExisting(false);
      }
    };

    checkAndLoadExistingReview();
  }, [academicYear, semesterDbValue, navigation, course.id]);

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      if (prev.length >= 4) {
        setShowTagLimitSnack(true);
        return prev;
      }
      return [...prev, tagId];
    });
  };

  const canSubmit = workload !== null && format !== null && examDifficulty !== null && examType !== null;

  const handleGoToProfileSettings = () => {
    navigation.navigate('TabRoot', {
      screen: 'Settings',
      params: { openProfileDialog: true },
    });
  };

  const handleSubmit = async () => {
    if (!isReviewPeriodOpen(academicYear, semesterDbValue)) {
      setPeriodClosed(true);
      Alert.alert(t("受付期間外です"), t("現在、この授業の講義レビューは受け付けていません。"));
      return;
    }
    if (!auth.currentUser) {
      Alert.alert(t("ログインが必要です"), t("ログイン状態が確認できませんでした。再度ログインしてからお試しください。"));
      return;
    }
    if (profileIncomplete || !reviewerProfile) {
      setProfileIncomplete(true);
      return;
    }
    if (!canSubmit) {
      Alert.alert(t("入力不足"), t("5段階評価3項目と期末試験形態を選択してください"));
      return;
    }
    if (!isCourseAdded(course.id, course.academicYear)) {
      Alert.alert(t("投稿できません"), t("この授業は現在の時間割に登録されていません。時間割に追加してから投稿してください。"));
      return;
    }

    try {
      setSubmitting(true);

      const reviewRef = doc(offeringDocument(db, course), 'reviews', auth.currentUser.uid);
      const indexRef = reviewIndexDocument(db, auth.currentUser.uid, course);
      const createdAt = hasExistingReview && existingReviewCreatedAt !== undefined
        ? existingReviewCreatedAt
        : serverTimestamp();
      const updatedAt = serverTimestamp();

      const payload = {
        workload,
        format,
        examDifficulty,
        examType,
        tags: selectedTags,
        reviewerYear: reviewerProfile.year,
        reviewerFaculty: reviewerProfile.faculty,
        academicYear,
        semester: semesterDbValue,
        createdAt,
        updatedAt,
      };

      const batch = writeBatch(db);
      batch.set(reviewRef, payload, { merge: true });
      batch.set(indexRef, {
        courseId: getCourseId(course),
        academicYear,
        semester: semesterDbValue,
        reviewPath: reviewRef.path,
        reviewerYear: reviewerProfile.year,
        reviewerFaculty: reviewerProfile.faculty,
        createdAt,
        updatedAt,
      }, { merge: true });
      await batch.commit();

      // レビュー本体の保存はここで成功しているため、即座にキャッシュを無効化する
      firestoreCache.invalidate(CACHE_KEYS.courseReviews(getOfferingKey(course), academicYear));
      firestoreCache.invalidate(`${CACHE_KEYS.courseReviews(getOfferingKey(course), academicYear)}:details`);

      // 新規投稿の場合のみ、レビューゲートの件数・状態を即時反映する（既存レビューの更新では加算しない）
      if (!hasExistingReview) {
        notifyReviewSubmitted();
      }

      Alert.alert(hasExistingReview ? t("更新完了") : t("投稿完了"), hasExistingReview ? t("レビューを更新しました") : t("レビューを投稿しました"));

      navigation.navigate('ReviewManagement');
    } catch (error) {
      console.error('[CourseReviewFormScreen] submit failed:', error);
      Alert.alert(t("エラー"), t(getSubmitErrorMessage(error)));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingExisting) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 12, color: theme.colors.onSurfaceVariant }}>
          {t("既存のレビューを読み込み中...")}
        </Text>
      </View>
    );
  }

  if (periodClosed) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: theme.colors.background, padding: 24 }]}>
        <Card style={{ width: '100%' }}>
          <Card.Content>
            <Text style={[styles.title, { color: theme.colors.onSurface }]}>{t("レビュー受付期間外です")}</Text>
            <Text style={{ marginTop: 8, color: theme.colors.onSurfaceVariant }}>
              {t("現在、この年度・学期の講義レビューは受け付けていません。")}
            </Text>
            <Button mode="contained" style={{ marginTop: 16 }} onPress={() => navigation.goBack()}>
              {t("戻る")}
            </Button>
          </Card.Content>
        </Card>
      </View>
    );
  }

  if (profileIncomplete) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: theme.colors.background, padding: 24 }]}>
        <Card style={{ width: '100%' }}>
          <Card.Content>
            <Text style={[styles.title, { color: theme.colors.onSurface }]}>{t("学年・学部が未設定です")}</Text>
            <Text style={{ marginTop: 8, color: theme.colors.onSurfaceVariant }}>
              {t("学年・学部が未設定のためレビューを投稿できません。設定画面で登録してください。")}
            </Text>
            <Button mode="contained" style={{ marginTop: 16 }} onPress={handleGoToProfileSettings}>
              {t("設定へ移動")}
            </Button>
            <Button mode="text" style={{ marginTop: 8 }} onPress={() => navigation.goBack()}>
              {t("戻る")}
            </Button>
          </Card.Content>
        </Card>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]} contentContainerStyle={styles.content}>
      <Card>
        <Card.Content>
          <Text style={[styles.title, { color: theme.colors.onSurface }]}>{hasExistingReview ? t("講義レビュー編集") : t("講義レビュー投稿")}</Text>
          <Text style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>{course.name}</Text>
          <Text style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
            {t('{0}年度 / {1}{2}', [academicYear, t(semesterDisplay), dayPeriodString ? ` / ${t(dayPeriodString)}` : ''])}
          </Text>
        </Card.Content>
      </Card>

      <Card style={styles.card}>
        <Card.Content>
          <ScaleSelector
            label={t("課題の重さ")}
            minLabel="軽い"
            maxLabel="重い"
            value={workload}
            onChange={setWorkload}
            colors={BLUE_GRAY_SCALE}
          />

          <ScaleSelector
            label={t("授業の形式")}
            minLabel="講義"
            maxLabel="発表/議論"
            value={format}
            onChange={setFormat}
            colors={COURSE_FORMAT_SCALE}
          />

          <ScaleSelector
            label={t("期末試験/レポート難易度")}
            minLabel="易しい"
            maxLabel="難しい"
            value={examDifficulty}
            onChange={setExamDifficulty}
            colors={BLUE_GRAY_SCALE}
          />

          <Text style={[styles.fieldTitle, { color: theme.colors.onSurface }]}>{t("期末試験形態")}</Text>
          <View style={styles.examTypeRow}>
            {[
              { value: 'in_person', label: t("対面試験") },
              { value: 'online', label: t("オンライン試験") },
              { value: 'none', label: t("試験なし") },
            ].map((item) => {
              const selected = examType === item.value;
              return (
                <Chip
                  key={item.value}
                  mode={selected ? 'flat' : 'outlined'}
                  selected={selected}
                  onPress={() => setExamType(item.value as ExamType)}
                  showSelectedCheck={false}
                  style={[
                    styles.optionChip,
                    selected
                      ? { backgroundColor: theme.colors.primaryContainer, borderColor: 'transparent' }
                      : { borderColor: theme.colors.outline },
                  ]}
                  textStyle={{
                    color: selected ? theme.colors.onPrimaryContainer : theme.colors.onSurface,
                    fontWeight: selected ? 'bold' : '600',
                  }}
                >
                  {t(item.label)}
                </Chip>
              );
            })}
          </View>

          <Text style={[styles.fieldTitle, { color: theme.colors.onSurface }]}>{t("特徴タグ（雰囲気＋学習内容で最大4つ）")}</Text>
          {tagsLoading ? (
            <ActivityIndicator style={{ marginVertical: 8 }} />
          ) : (
            <>
              <Text style={[styles.tagGroupLabel, { color: theme.colors.onSurfaceVariant }]}>{t("雰囲気タグ")}</Text>
              <View style={styles.tagWrap}>
                {atmosphereTags.map((tag) => {
                  const selected = selectedTags.includes(tag.id);
                  const disabled = !selected && selectedTags.length >= 4;
                  return (
                    <Chip
                      key={tag.id}
                      mode={selected ? 'flat' : 'outlined'}
                      selected={selected}
                      disabled={disabled}
                      onPress={() => toggleTag(tag.id)}
                      showSelectedCheck={false}
                      style={[
                        styles.optionChip,
                        selected ? { backgroundColor: theme.colors.primaryContainer, borderColor: 'transparent' } : null,
                      ]}
                      textStyle={
                        selected ? { color: theme.colors.onPrimaryContainer, fontWeight: 'bold' } : undefined
                      }
                    >
                      {t(tag.name)}
                    </Chip>
                  );
                })}
              </View>

              <Text style={[styles.tagGroupLabel, { color: theme.colors.onSurfaceVariant, marginTop: 12 }]}>{t("学習内容タグ")}</Text>
              <View style={styles.tagWrap}>
                {contentTags.map((tag) => {
                  const selected = selectedTags.includes(tag.id);
                  const disabled = !selected && selectedTags.length >= 4;
                  return (
                    <Chip
                      key={tag.id}
                      mode={selected ? 'flat' : 'outlined'}
                      selected={selected}
                      disabled={disabled}
                      onPress={() => toggleTag(tag.id)}
                      showSelectedCheck={false}
                      style={[
                        styles.optionChip,
                        selected ? { backgroundColor: theme.colors.primaryContainer, borderColor: 'transparent' } : null,
                      ]}
                      textStyle={
                        selected ? { color: theme.colors.onPrimaryContainer, fontWeight: 'bold' } : undefined
                      }
                    >
                      {t(tag.name)}
                    </Chip>
                  );
                })}
              </View>
            </>
          )}

          <Button mode="contained" onPress={handleSubmit} loading={submitting} disabled={submitting || !canSubmit}>
            {hasExistingReview ? t("更新する") : t("投稿する")}
          </Button>
        </Card.Content>
      </Card>

      <Snackbar visible={showTagLimitSnack} onDismiss={() => setShowTagLimitSnack(false)} duration={2200}>
        {t("タグは最大4つまで選択できます")}
      </Snackbar>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 30,
    gap: 12,
  },
  card: {
    marginTop: 4,
  },
  selectorSection: {
    marginBottom: 14,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
  },
  description: {
    marginTop: 6,
    opacity: 0.7,
  },
  fieldTitle: {
    marginTop: 10,
    marginBottom: 8,
    fontWeight: '700',
  },
  scaleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginHorizontal: 16,
  },
  scaleTip: {
    fontSize: 12,
  },
  scaleButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  examTypeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 10,
  },
  tagGroupLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  tagWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 14,
  },
  optionChip: {
    marginBottom: 6,
  },
});

export default CourseReviewFormScreen;
